import {
  useEffect,
  useState,
  useRef,
  useCallback,
  FocusEventHandler,
} from 'react'
import { useMachine } from '@xstate/react'
import { searchNetsuiteMachine } from 'machines'
import { Box, Button, Label, Close, IconButton, Flex } from 'theme-ui'
import { InfoIcon, Loading, Tooltip } from 'components'
import { InputWithAdornment } from 'components/Form'
import { SearchNetSuiteResults } from './'
import { useClickOutside, useHandleEscapeKey } from 'hooks'
import { noop } from 'utils'

export type SearchNetSuiteProps = {
  initialQuery?: string
  onItemClick?: (field: string, value: string) => void
  onLink: (data: any) => void
  setError: (error: string) => void
  onBlur?: FocusEventHandler
  tabIndex?: number
  disableNetSuite: boolean
}

export function SearchNetSuite({
  initialQuery = '',
  onLink = noop,
  setError,
  onBlur,
  tabIndex,
  disableNetSuite,
}: SearchNetSuiteProps) {
  const componentRef = useRef<HTMLDivElement>(null)
  const searchInputRef = useRef<HTMLInputElement>(null)

  const [current, send] = useMachine(searchNetsuiteMachine)
  const [query, setQuery] = useState<string>('')
  const [showResults, setShowResults] = useState<boolean>(false)

  const hideResults = useCallback(() => {
    setShowResults(false)
  }, [setShowResults])
  useClickOutside(componentRef, hideResults)
  useHandleEscapeKey(hideResults)

  const resetSearch = useCallback(
    (evt: any) => {
      setQuery('')
      setError('')
      searchInputRef.current?.focus()
      hideResults()
    },
    [setQuery, setError, hideResults]
  )

  const ResetSearchButton = useCallback(
    () => (
      <Box sx={{ cursor: 'pointer' }} onClick={resetSearch}>
        <Close type="button" />
      </Box>
    ),
    [resetSearch]
  )

  useEffect(() => {
    if (initialQuery && initialQuery !== query) {
      setQuery(initialQuery)
      send('SEARCH_CHANGE', { query: initialQuery })
    }
    // eslint-disable-next-line
  }, [initialQuery])

  useEffect(() => {
    if (current.matches('failed')) {
      setError(current.context.error ?? '')
    }
    if (current.matches('hasResults') || current.matches('noResults')) {
      searchInputRef.current?.focus()
    }
    if (
      current.matches('debouncing') ||
      current.matches('searching') ||
      current.matches('idle')
    ) {
      setShowResults(false)
    }
  }, [current, current.value, setError, setShowResults])

  function handleInputChange({ target }: any) {
    const { value } = target
    if (value === '') setError('')
    setQuery(value)
    hideResults()
  }

  function validateQuery() {
    if (
      !query ||
      !query.length ||
      (!query.includes('@') && query.trim().split(' ').length < 2) ||
      //TODO validate email
      (query.includes('@') && !query.includes('.'))
    ) {
      return false
    }
    return true
  }

  function handleNetSuiteSearch(evt: any) {
    evt.preventDefault()
    evt.stopPropagation()
    if (!validateQuery()) {
      setShowResults(false)
      setError('Please enter a valid name or email')
      return
    }
    send('SEARCH_CHANGE', { query })
    setError('')
  }

  function handleSearchItemClick(record: any) {
    setShowResults(false)
    onLink({
      id: record.id,
      forename: record.forename,
      surname: record.surname,
      email: record.email,
    })
  }

  const handleFocus = () => {
    if (!current.matches('idle')) setShowResults(true)
  }

  return (
    <Box>
      <Box>
        <Label
          sx={{
            display: 'inline',
          }}>
          Find your student from Netsuite
        </Label>
        <Tooltip
          variant="netsuiteExplanation"
          width={303}
          tooltipSx={{ left: 32, bottom: 2 }}>
          <IconButton
            type="button"
            variant={`icon.primary`}
            sx={{ outline: 'none' }}>
            <InfoIcon />
          </IconButton>
        </Tooltip>
      </Box>
      <Flex
        sx={{ flexWrap: 'wrap', justifyContent: ['center', 'unset'] }}
        ref={componentRef}>
        <Box
          sx={{
            minWidth: ['100%', '80%'],
            display: 'inline-block',
            position: 'relative',
          }}>
          <InputWithAdornment
            ref={searchInputRef}
            tabIndex={tabIndex}
            aria-label="search-input"
            disabled={disableNetSuite || current.matches('searching')}
            value={query}
            onChange={handleInputChange}
            onFocus={handleFocus}
            onBlur={onBlur}
            placeholder="Search by email or first + last name"
            sx={{ position: 'relative' }}
            after={query.length ? <ResetSearchButton /> : null}
            isAfterClickable={!!query.length}
          />
          {showResults && (
            <Box
              style={{
                paddingLeft: '10px',
                paddingRight: '10px',
                width: '100%',
                position: 'absolute',
                zIndex: 10,
                top: 40,
              }}>
              <SearchNetSuiteResults
                results={
                  current.matches('hasResults') ? current.context.results : []
                }
                noResults={current.matches('noResults')}
                onSearchItemClick={handleSearchItemClick}
              />
            </Box>
          )}
        </Box>
        <Button
          variant="buttons.primary"
          sx={{
            position: 'relative',
            ml: 2,
            mt: [2, 0],
            width: [150, 'unset'],
          }}
          type="submit"
          aria-label="search-button"
          disabled={disableNetSuite || current.matches('searching')}
          onClick={handleNetSuiteSearch}>
          {current.matches('searching') ? <Loading /> : 'Search'}
        </Button>
      </Flex>
    </Box>
  )
}
